import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";

const BACKGROUND_IMAGE_URL = 'https://i.postimg.cc/nhPDPjqg/Chat-GPT-Image-25-Noe-2025-10-42-19-p-m.png?auto=compress&cs=tinysrgb&w=1200';
const DEFAULT_LOGO_URL = "https://i.postimg.cc/Lswv6VPV/Chat-GPT-Image-3-Dek-2025-12-44-21-m-m.png";

export default function GuestNameInterface({ hotelLogoPath, displayedHotelName, onNameSubmit }) {
    const [name, setName] = useState("");
    const [lastName, setLastName] = useState("");
    const [bgLoaded, setBgLoaded] = useState(false);
    const [logoLoaded, setLogoLoaded] = useState(false);

    const logoUrl = hotelLogoPath || DEFAULT_LOGO_URL;

    // Preload background image
    useEffect(() => {
        const img = new Image();
        img.src = BACKGROUND_IMAGE_URL;
        img.onload = () => setBgLoaded(true);
        img.onerror = () => setBgLoaded(true);
    }, []);

    // Preload hotel logo
    useEffect(() => {
        const img = new Image();
        img.src = logoUrl;
        img.onload = () => setLogoLoaded(true);
        img.onerror = () => setLogoLoaded(true);
    }, [logoUrl]);

    const handleContinue = (e) => {
        e.preventDefault();
        if (!name.trim() || !lastName.trim()) return;
        onNameSubmit(name, lastName);
    };

    const inputBase =
        "w-full rounded-2xl border border-black/10 bg-white/90 px-4 py-3 text-sm text-neutral-900 placeholder:text-neutral-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-black/10 transition-all";

    // Show loading screen until all images load
    if (!bgLoaded || !logoLoaded) {
        return (
            <div className="h-[100dvh] w-full flex items-center justify-center bg-neutral-900">
                <Loader2 className="w-8 h-8 animate-spin text-white/50" />
            </div>
        );
    }

    return (
        <div className="h-[100dvh] w-full flex items-center justify-center bg-neutral-900 text-white relative overflow-hidden">
            {/* Background image */}
            <div
                className="absolute inset-0 bg-cover bg-center animate-fade-in"
                style={{
                    backgroundImage: `url('${BACKGROUND_IMAGE_URL}')`,
                }}
            />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/65 to-black/80" />

            {/* Content */}
            <div className="relative z-10 w-full max-w-md px-4 py-6 flex flex-col gap-5">
                {/* Centered hotel logo */}
                <div className="flex justify-center mb-3">
                    <img
                        src={logoUrl}
                        alt={displayedHotelName || "Kypser logo"}
                        className="h-20 w-20 object-contain drop-shadow-[0_10px_32px_rgba(0,0,0,0.45)]"
                    />
                </div>

                {/* Dynamic Heading and Subtitle */}
                <div className="space-y-1 text-center">
                    <h1 className="text-2xl font-semibold tracking-tight">
                        We love names. What's yours?
                    </h1>
                    <p className="text-sm text-white/70">
                        Please use your first and last name as in your booking.
                    </p>
                </div>

                {/* Form Content - Direct child, no CardClass */}
                <form onSubmit={handleContinue} className="flex flex-col gap-5 w-full max-w-xs mx-auto">

                    {/* 1. Name Input block */}
                    <div className="space-y-3">
                        <input
                            type="text"
                            className={inputBase}
                            placeholder="First Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoComplete="given-name"
                            required
                            autoFocus
                        />
                        <input
                            type="text"
                            className={inputBase}
                            placeholder="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            autoComplete="family-name"
                            required
                        />
                    </div>

                    {/* 2. Button & Helper Text block */}
                    <div className="flex flex-col gap-3">
                        <button
                            type="submit"
                            disabled={!name.trim() || !lastName.trim()}
                            className="inline-flex w-full items-center justify-center rounded-2xl bg-black text-[15px] font-medium text-white py-3 shadow-[0_14px_30px_rgba(0,0,0,0.3)] disabled:opacity-95 disabled:cursor-not-allowed transition-transform"
                        >
                            Continue
                        </button>

                        <p className="text-[11px] leading-relaxed text-white/70 text-center">
                            We'll use this name to customize messages and requests.
                        </p>
                    </div>
                </form>

            </div>

            {/* Footer - positioned at the bottom */}
            <div className="absolute bottom-4 left-0 right-0 text-center text-[11px] text-white/50 z-20">
                Powered by <a
                    href="https://kypser.com/"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-white underline underline-offset-2"
                >
                    Kypser
                </a>
            </div>
        </div>
    );
}

